/**
 * Shared city extraction for Google address components (Geocoding API,
 * Places API v1, and the Maps JS SDK all use the same component type names).
 *
 * Not every US address has a `locality` component. Notably, Michigan charter
 * townships (e.g. "Shelby Charter Township") come back as
 * `administrative_area_level_3` with NO locality, and some addresses only
 * carry a sublocality or neighborhood. Every parser in the app must use the
 * same priority-ordered fallback chain, otherwise the city ends up empty and
 * address validation fails with "Missing required address fields".
 */

export interface AddressComponentCandidate {
  types: string[];
  /** Display name for the component (long_name / longText). */
  name: string;
}

/**
 * Component types that can serve as the "city", from most to least preferred.
 * `postal_town` covers UK-style addresses; `administrative_area_level_3`
 * covers US townships; `administrative_area_level_2` (county) is a last
 * resort so downstream validation has something to work with.
 */
const CITY_TYPE_PRIORITY: readonly string[] = [
  'locality',
  'postal_town',
  'sublocality_level_1',
  'sublocality',
  'administrative_area_level_3',
  'neighborhood',
  'administrative_area_level_2',
];

/**
 * Pick the best "city" value from Google address components using the
 * priority chain above. Returns '' when nothing usable is present.
 */
export function extractCityFromComponents(components: AddressComponentCandidate[]): string {
  let bestName = '';
  let bestRank = CITY_TYPE_PRIORITY.length;

  for (const component of components) {
    if (!component.name) continue;
    for (const type of component.types) {
      const rank = CITY_TYPE_PRIORITY.indexOf(type);
      if (rank !== -1 && rank < bestRank) {
        bestRank = rank;
        bestName = component.name;
      }
    }
  }

  return bestName;
}

/**
 * Designators that already mark a value as a secondary unit, so it can be kept
 * exactly as the customer typed it ("Apt 4B", "Suite 200", "#1012").
 */
const UNIT_DESIGNATOR_REGEX =
  /^(#|apt|apartment|suite|ste|unit|no\.?|number|bldg|building|fl|floor|rm|room|lot|space|spc|trlr|trailer|dept|department|basement|bsmt|penthouse|ph|front|rear|upper|lower)\b/i;

/**
 * Label a bare unit value so it doesn't read as part of the street. Customers
 * often type just "55" or "b", which would render as "111 SE 17th St 55" —
 * ambiguous both to the customer and to USPS. Anything already carrying a
 * designator is left untouched.
 */
export function normalizeAddressUnit(unit?: string): string {
  const cleaned = unit?.trim().replace(/\s+/g, ' ') ?? '';
  if (!cleaned) return '';
  if (UNIT_DESIGNATOR_REGEX.test(cleaned)) return cleaned;

  // Only bare tokens are ambiguous. Free text ("north wing") is left alone —
  // prefixing it with "#" would read worse, not better.
  const isBareToken = /^[a-z0-9-]{1,6}$/i.test(cleaned);
  return isBareToken ? `#${cleaned.toUpperCase()}` : cleaned;
}

/**
 * Fold a unit (apt/suite) into a Google formatted address so confirmation
 * text matches what the customer entered. Google's formatted address usually
 * omits the unit, and it always leads with the street segment.
 */
export function insertUnitIntoFormattedAddress(formattedAddress: string, unit?: string): string {
  const trimmedUnit = unit?.trim();
  if (!trimmedUnit) return formattedAddress;
  if (formattedAddress.toLowerCase().includes(trimmedUnit.toLowerCase())) return formattedAddress;

  const parts = formattedAddress.split(',');
  parts[0] = `${parts[0]} ${trimmedUnit}`;
  return parts.join(',');
}
